import React from "react";
import styled, { useTheme } from "styled-components";
import {
  ChangeApprovalStatus,
  WorkContractStatus,
} from "../../redux/api-model/employees/workContract";
import {
  BudgetStatus,
  DocumentStatus,
} from "../../redux/api-model/employees/absence";
import { TextVariant, TypographyNew } from "../typography/typography";
import { useTranslation } from "react-i18next";
import { WorkContractTaskStatus } from "../../redux/employees/tasks/tasks";
import { FlexContainer } from "../../accounts/company/pages/employees/calendar/common/styles";
import { ConnectionStatus } from "../../redux/api-model/general/auth";
import { ApprovalStatus } from "../../redux/api-model/employees/approval-statuses";

const PRIMARY_VARIANT = "primary";
const SECONDARY_VARIANT = "secondary";

export const StatusContainer = styled.div<{
  backgroundColor: string;
  color: string;
  margin?: string;
}>`
  min-width: 84px;
  max-width: 100px;
  height: 28px;
  display: flex;
  margin: ${(props) => (props.margin ? props.margin : "0")};
  border-radius: ${(props) => props.theme.borderRadius.small};
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
  padding: 0 10px;

  &.${SECONDARY_VARIANT} {
    height: 20px;
    width: 20px;
    min-width: unset;
    margin-left: 20px;
    padding: 0;
    flex: unset;
    border: 1px solid ${(props) => props.theme.colors.grays4};
  }
`;

export enum Status {
  WITHDRAWN = "WITHDRAWN",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  APPROVED = "APPROVED",
  PROVIDED = "PROVIDED",
}

const WARNING_INLINE_STATUS = "warning";
const SUCCESS_INLINE_STATUS = "success";
const ERROR_INLINE_STATUS = "error";

export type InlineStatus =
  | typeof WARNING_INLINE_STATUS
  | typeof SUCCESS_INLINE_STATUS
  | typeof ERROR_INLINE_STATUS;

interface StatusChipProps {
  text?: string;
  children?: JSX.Element;
  className?: string;
  status:
    | Status
    | ChangeApprovalStatus
    | WorkContractStatus
    | ApprovalStatus
    | DocumentStatus
    | BudgetStatus
    | WorkContractTaskStatus
    | InlineStatus
    | ConnectionStatus;
  margin?: string;
  variant?: typeof PRIMARY_VARIANT | typeof SECONDARY_VARIANT;
}

const StatusChip = ({
  margin,
  status,
  children,
  text: label,
  className,
  variant = PRIMARY_VARIANT,
}: StatusChipProps): JSX.Element => {
  let backgroundColor: string;
  let color: string;
  let text: string;
  const { t } = useTranslation("statuses");
  const theme = useTheme();
  const as = children ? "div" : "p";

  switch (status) {
    case SUCCESS_INLINE_STATUS:
    case ChangeApprovalStatus.APPROVED:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.approved");
      }
      break;
    case ApprovalStatus.APPROVED:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.approved");
      }
      break;
    case Status.PROVIDED:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.documentProvided");
      }
      break;
    case DocumentStatus.PROVIDED:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.provided");
      }
      break;
    case ChangeApprovalStatus.REQUESTED:
      {
        color = theme.colors.accent6;
        backgroundColor = theme.colors.accent5;
        text = t("statuses.requested");
      }
      break;
    case WARNING_INLINE_STATUS:
    case ApprovalStatus.REQUESTED:
      {
        color = theme.colors.accent6;
        backgroundColor = theme.colors.accent5;
        text = t("statuses.requested");
      }
      break;
    case DocumentStatus.ELECTRONIC_CERTIFICATE:
      {
        color = theme.colors.primary1;
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.electronic_certificate");
      }
      break;
    case DocumentStatus.ELECTRONIC_CERTIFICATE_RECEIVED:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.electronic_certificate_received");
      }
      break;
    case DocumentStatus.ELECTRONIC_CERTIFICATE_REQUESTED:
      {
        color = theme.colors.accent6;
        backgroundColor = theme.colors.accent5;
        text = t("statuses.electronic_certificate_requested");
      }
      break;
    case DocumentStatus.ELECTRONIC_CERTIFICATE_FOLLOWUP_REQUESTED:
      {
        color = theme.colors.accent6;
        backgroundColor = theme.colors.accent5;
        text = t("statuses.electronic_certificate_followup_requested");
      }
      break;
    case ApprovalStatus.DRAFT:
      {
        color = theme.colors.primary1;
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.draft");
      }
      break;
    case ApprovalStatus.NOT_REQUIRED:
      {
        color = theme.colors.primary1;
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.not_required");
      }
      break;
    case ApprovalStatus.LOCKED:
      {
        color = theme.colors.primary1;
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.locked");
      }
      break;
    case DocumentStatus.NOT_REQUIRED:
      {
        color = theme.colors.black;
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.not_required");
      }
      break;
    case ERROR_INLINE_STATUS:
    case ChangeApprovalStatus.REJECTED:
      {
        color = theme.colors.accent1;
        backgroundColor = theme.colors.alerts5;
        text = t("statuses.rejected");
      }
      break;
    case DocumentStatus.MISSING:
      {
        color = theme.colors.accent1;
        backgroundColor = theme.colors.alerts5;
        text = t("statuses.missing");
      }
      break;
    case ApprovalStatus.REJECTED:
      {
        color = theme.colors.accent1;
        backgroundColor = theme.colors.alerts5;
        text = t("statuses.rejected");
      }
      break;
    case WorkContractTaskStatus.DONE:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.done");
      }
      break;
    case WorkContractTaskStatus.OPEN:
      {
        color = theme.colors.accent6;
        backgroundColor = theme.colors.accent5;
        text = t("statuses.open");
      }
      break;
    case WorkContractStatus.ACTIVE:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.active");
      }
      break;
    case WorkContractStatus.EXPIRING_14:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.expiring");
      }
      break;
    case WorkContractStatus.FUTURE:
      {
        color = "grey";
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.future");
      }
      break;
    case WorkContractStatus.EXPIRING_30:
      {
        color = "black";
        backgroundColor = theme.colors.grays4;
        text = t("statuses.resigned");
      }
      break;
    case WorkContractStatus.FORMER:
      {
        color = theme.colors.black;
        backgroundColor = theme.colors.grays4;
        text = t("statuses.resigned");
      }
      break;
    case BudgetStatus.UPCOMING:
      {
        color = "grey";
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.future");
      }
      break;
    case BudgetStatus.EXPIRED:
      {
        color = "black";
        backgroundColor = theme.colors.grays4;
        text = t("statuses.expired");
      }
      break;
    case BudgetStatus.EXPIRED_CARRY_OVER:
      {
        color = "black";
        backgroundColor = theme.colors.grays4;
        text = t("statuses.expiredCarryOver");
      }
      break;
    case BudgetStatus.VALID:
      {
        color = theme.colors.accent8;
        backgroundColor = theme.colors.alerts4;
        text = t("statuses.active");
      }
      break;
    case WorkContractStatus.FUTURE_14:
      {
        color = "grey";
        backgroundColor = theme.colors.alerts1;
        text = t("statuses.starting");
      }
      break;
    default: {
      color = "grey";
      backgroundColor = theme.colors.alerts1;
      text = t("statuses.unknown");
    }
  }

  const title = label ?? text;

  if (variant === SECONDARY_VARIANT) {
    return (
      <FlexContainer className={className}>
        <StatusContainer
          className={variant}
          color={color}
          backgroundColor={backgroundColor}
          margin={margin}
        />
        <TypographyNew
          as={as}
          title={title}
          marginLeft="10px"
          variant={TextVariant.body2}
          noWrap
        >
          {children ?? title}
        </TypographyNew>
      </FlexContainer>
    );
  }

  return (
    <StatusContainer
      color={color}
      className={`${variant} ${className}`}
      backgroundColor={backgroundColor}
      margin={margin}
    >
      <TypographyNew
        as={as}
        title={title}
        variant={TextVariant.body2}
        color={color}
        noWrap
      >
        {children ?? title}
      </TypographyNew>
    </StatusContainer>
  );
};

export default StatusChip;
